import { KeyboardEvent, forwardRef, memo } from 'react'
import classNames from 'classnames'
import moment from 'jalali-moment'
import { AnimatePresence, motion } from 'framer-motion'
import { FiChevronLeft, FiMoreVertical } from 'react-icons/fi'
import { LuCheckCheck, LuUserPlus } from 'react-icons/lu'
import { fundName } from 'utilities/helper/format'
import { MessageTarget } from '../messageTarget'
import { CATEGORY_META, CategoryMeta } from '../categories'

// An invitation is about joining, whatever the sentence says, and it is the
// one kind whose card should read as such at a glance.
const INVITE_META: CategoryMeta = {
    ...CATEGORY_META.fund,
    label: 'دعوت‌نامه',
    title: 'دعوت به صندوق',
    Icon: LuUserPlus,
    tile: 'bg-blue-6 text-blue-1',
}

export const messageMeta = (message: IInboxMessage, target: MessageTarget) =>
    target.kind === 'invite'
        ? INVITE_META
        : CATEGORY_META[message.category] ?? CATEGORY_META.fund

export const messageTime = (timestamp: number) =>
    moment(new Date(timestamp * 1000))
        .locale('fa')
        .format('HH:mm')

/** Cards after this many enter together; nobody watches a 20-step cascade. */
const STAGGER_CAP = 8

interface MessageCardProps {
    message: IInboxMessage
    target: MessageTarget
    /** Unseen when this visit began — the card stays warm after it is read. */
    fresh: boolean
    /** Position on screen, for the entrance and the read wave. */
    index: number
    onOpen: (message: IInboxMessage, target: MessageTarget) => void
    onMenu: (message: IInboxMessage) => void
}

/**
 * One inbox message.
 *
 * The whole card is the tap target when the message leads somewhere, and the
 * action row spells out where. A message with nowhere to go has no action
 * row, so a card that looks tappable always is. Deleting lives behind the ⋮
 * button rather than Android's long-press, which nobody discovers on the web.
 *
 * Beside the time: a yellow square while the message is unread, a green
 * double tick once it is — read here or on any other device. The page marks
 * what it shows as read a moment after it opens, so the squares turn into
 * ticks in a short wave from the top.
 */
const MessageCard = forwardRef<HTMLElement, MessageCardProps>(
    function MessageCard(
        { message, target, fresh, index, onOpen, onMenu },
        ref
    ) {
        const meta = messageMeta(message, target)
        const { Icon } = meta
        const clickable = Boolean(target.destination)
        const title = target.boxName ? fundName(target.boxName) : meta.title
        const time = messageTime(message.timestamp)
        const order = Math.min(index, STAGGER_CAP)

        const open = () => {
            if (clickable) onOpen(message, target)
        }
        const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
            if (!clickable || event.target !== event.currentTarget) return
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                open()
            }
        }

        return (
            <motion.article
                ref={ref}
                layout="position"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.18 } }}
                transition={{
                    duration: 0.32,
                    ease: [0.16, 1, 0.3, 1],
                    delay: order * 0.035,
                }}
                role={clickable ? 'link' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={open}
                onKeyDown={onKeyDown}
                aria-label={clickable ? `${title}، ${target.cta}` : undefined}
                className={classNames(
                    'relative overflow-hidden rounded-card border p-3.5 transition-colors duration-500 md:p-4',
                    !message.is_seen
                        ? 'border-warning-1/40 bg-white shadow-badge'
                        : fresh
                        ? 'border-green-1/30 bg-brand-soft/50'
                        : 'border-divider bg-white',
                    clickable &&
                        'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-green-1/50 active:bg-sunken'
                )}
            >
                {/* The accent on the reading edge follows the marker. */}
                {(fresh || !message.is_seen) && (
                    <span
                        aria-hidden
                        className={classNames(
                            'absolute inset-y-3 start-0 w-[3px] rounded-e-full transition-colors duration-500',
                            message.is_seen ? 'bg-green-1' : 'bg-warning-1'
                        )}
                    />
                )}

                <div className="flex items-start gap-2.5 md:gap-3">
                    <span
                        className={classNames(
                            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl md:h-10 md:w-10',
                            meta.tile
                        )}
                        aria-hidden
                    >
                        <Icon className="h-[18px] w-[18px] md:h-5 md:w-5" />
                    </span>

                    <div className="min-w-0 flex-1 pt-0.5">
                        <h3 className="truncate text-[13px] font-bold text-heading md:text-sm">
                            {title}
                        </h3>
                        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-text-secondary md:text-xs">
                            <ReadMarker seen={message.is_seen} order={order} />
                            <span className="truncate">
                                {target.boxName || target.kind === 'invite'
                                    ? `${meta.label} · ${time}`
                                    : time}
                            </span>
                        </p>
                    </div>

                    <button
                        type="button"
                        aria-label="گزینه‌های پیام"
                        onClick={(event) => {
                            event.stopPropagation()
                            onMenu(message)
                        }}
                        className="-me-2 -mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors active:bg-divider"
                    >
                        <FiMoreVertical className="h-[18px] w-[18px]" />
                    </button>
                </div>

                <p className="mt-2.5 whitespace-pre-line break-words text-[13px] leading-6 text-text-body md:mt-3 md:text-sm md:leading-7">
                    {message.body}
                </p>

                {clickable && (
                    <div
                        className={classNames(
                            'mt-2.5 flex items-center justify-between border-t pt-2.5 md:mt-3 md:pt-3',
                            fresh ? 'border-green-1/20' : 'border-divider'
                        )}
                    >
                        <span className="text-[13px] font-bold text-green-1 md:text-sm">
                            {target.cta}
                        </span>
                        <FiChevronLeft
                            className="h-4 w-4 text-green-1 md:h-[18px] md:w-[18px]"
                            aria-hidden
                        />
                    </div>
                )}
            </motion.article>
        )
    }
)

export default memo(MessageCard)

/**
 * Unread: a small yellow square with a soft pulse. Read: Hamyan's green
 * double tick. When a message is read the square folds away and the tick
 * springs in, each card a beat after the one above it.
 */
export function ReadMarker({ seen, order = 0 }: { seen: boolean; order?: number }) {
    return (
        <span
            role="img"
            aria-label={seen ? 'خوانده‌شده' : 'خوانده‌نشده'}
            className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center"
        >
            <AnimatePresence initial={false} mode="wait">
                {seen ? (
                    <motion.span
                        key="seen"
                        initial={{ scale: 0.2, opacity: 0, rotate: -25 }}
                        animate={{ scale: 1, opacity: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 520, damping: 20 }}
                        className="flex"
                    >
                        <LuCheckCheck
                            className="h-4 w-4 text-green-1"
                            strokeWidth={2.6}
                        />
                    </motion.span>
                ) : (
                    <motion.span
                        key="unseen"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{
                            scale: 0,
                            rotate: 90,
                            opacity: 0,
                            transition: { duration: 0.2, delay: order * 0.07 },
                        }}
                        className="relative block h-2.5 w-2.5"
                    >
                        <span className="absolute inset-0 animate-ping rounded-[3px] bg-warning-1 opacity-50 [animation-duration:1.8s]" />
                        <span className="absolute inset-0 rounded-[3px] bg-warning-1" />
                    </motion.span>
                )}
            </AnimatePresence>
        </span>
    )
}

/** Same silhouette as a card, so the list does not jump when data lands. */
export function MessageCardSkeleton() {
    return (
        <div className="animate-pulse rounded-card border border-divider bg-white p-3.5 md:p-4">
            <div className="flex items-start gap-2.5 md:gap-3">
                <div className="h-9 w-9 shrink-0 rounded-xl bg-divider md:h-10 md:w-10" />
                <div className="flex-1 pt-1">
                    <div className="h-3 w-32 rounded bg-divider" />
                    <div className="mt-2 h-2.5 w-16 rounded bg-divider" />
                </div>
            </div>
            <div className="mt-4 h-3 w-full rounded bg-divider" />
            <div className="mt-2 h-3 w-4/5 rounded bg-divider" />
        </div>
    )
}
